import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { SearchContext, SearchParams } from '../models/search-request.interface';
import { LocalStorageService } from './localStorage.service';

const DEFAULT_PARAMS: Record<SearchContext, SearchParams> = {
  [SearchContext.Inline]: { query: '', categories: [], product: null, pageNo: 1, pageSize: 5 },
  [SearchContext.Results]: { query: '', categories: [], product: null, pageNo: 1, pageSize: 30 },
};

@Injectable()
export class SearchService {
  showSearch = new BehaviorSubject<boolean>(false);
  private paramsSubject = new BehaviorSubject<SearchParams>(DEFAULT_PARAMS[SearchContext.Inline]);
  private resultsSubject = new BehaviorSubject<any[]>([]);
  private allResultsSubject = new BehaviorSubject<any>({
    results: [],
    suggestedWord: null,
    currentQuery: '',
    isLoading: true,
  });
  private allResultsParamsSubject = new BehaviorSubject<SearchParams>(DEFAULT_PARAMS[SearchContext.Results]);
  private context: SearchContext = SearchContext.Inline;
  params$ = this.paramsSubject.asObservable();
  results$ = this.resultsSubject.asObservable();
  allResults$ = this.allResultsSubject.asObservable();
  allResultsParams$ = this.allResultsParamsSubject.asObservable();

  constructor(protected apiService: ApiService, private localStorage: LocalStorageService) {
    this.loadPersistedState();
  }

  private loadPersistedState() {
    const savedParams = this.localStorage.getItem('searchParams');
    if (savedParams) {
      try {
        const parsed = JSON.parse(savedParams);
        this.paramsSubject.next({
          ...parsed,
        });
      } catch {
        console.error('Failed to parse saved search parameters.');
      }
    }

    const savedResults = this.localStorage.getItem('searchResults');
    if (savedResults) {
      try {
        this.resultsSubject.next(JSON.parse(savedResults));
      } catch {
        this.resultsSubject.next([]);
      }
    }
  }

  setContext(context: SearchContext, isTryExperience?: boolean, resetCategories?: boolean) {
    this.context = context;
    const current = this.getParams();
    const defaults = DEFAULT_PARAMS[context];
    const updated: SearchParams = {
      ...defaults,
      ...current,
      query: current.query,
      categories: resetCategories ? [] : current.categories,
      product: current.product,
      pageNo: defaults.pageNo,
      pageSize: defaults.pageSize,
    };
    this.updateParams(updated, isTryExperience);
  }

  setParams(params: Partial<SearchParams>, targetContext?: SearchContext) {
    const context = targetContext ?? this.context;
    const defaults = DEFAULT_PARAMS[context];
    const isAllResults = targetContext === SearchContext.Results;
    const current = isAllResults ? this.allResultsParamsSubject.getValue() : this.paramsSubject.getValue();

    const updated: SearchParams = {
      ...defaults,
      ...current,
      ...params,
      query: params.query !== undefined ? params.query : current.query,
      pageSize: params.pageSize ?? current.pageSize ?? defaults.pageSize,
      pageNo: params.pageNo ?? current.pageNo ?? defaults.pageNo,
    };

    this.updateParams(updated, false, isAllResults);
  }

  private updateParams(params: SearchParams, isTryExperience?: boolean, isAllResults?: boolean) {
    const targetSubject = isAllResults ? this.allResultsParamsSubject : this.paramsSubject;
    const prev = targetSubject.getValue();

    if (!isTryExperience && JSON.stringify(prev) !== JSON.stringify(params)) {
      targetSubject.next(params);
    }
  }

  getParams(): SearchParams {
    return this.paramsSubject.getValue();
  }

  toggleSearch() {
    this.showSearch.next(!this.showSearch.value);
  }

  setResults(results: any[]) {
    this.resultsSubject.next(results);
    this.localStorage.setItem('searchResults', JSON.stringify(results));
  }

  clearSearchPersistence() {
    this.localStorage.removeItem('searchParams');
    this.localStorage.removeItem('searchResults');
    this.resultsSubject.next([]);
  }

  getSearchQueryParam(
    query: string,
    pageNo: number,
    pageSize: number,
    categories: string[] = [],
    localization?: string | null
  ): string {
    const params: string[] = [];
    if (query) params.push(`q=${encodeURIComponent(query)}`);
    if (pageNo) params.push(`pageNo=${pageNo}`);
    if (pageSize) params.push(`pageSize=${pageSize}`);
    if (localization) params.push(`localization=${encodeURIComponent(localization)}`);
    categories?.forEach((val) => params.push(`docCategory=${encodeURIComponent(val)}`));
    return params.join('&');
  }

  getOmniSearch(query: string): Observable<any> {
    return this.apiService.get('/devhub-genai/v1/search?' + query, undefined, false, undefined, undefined, true);
  }

  setAllResults(data: { results: any[]; suggestedWord: string | null; currentQuery: string; isLoading?: boolean }) {
    this.allResultsSubject.next({
      ...data,
      isLoading: data.isLoading ?? false,
    });
  }

  setAllResultsLoading(isLoading: boolean) {
    const current = this.allResultsSubject.getValue();
    this.allResultsSubject.next({
      ...current,
      isLoading,
    });
  }

  applyAllResultsCorrection(correctedQuery: string) {
    this.allResultsSubject.next({
      results: [],
      suggestedWord: null,
      currentQuery: correctedQuery,
      isLoading: true,
      isCorrectionUpdate: true,
    });
  }

  clearAllResults(isLoading: boolean = false) {
    this.allResultsSubject.next({
      results: [],
      suggestedWord: null,
      currentQuery: '',
      isLoading,
    });
  }
}
